import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { subscriptionAPI, emailMonitorAPI, alertsAPI } from '../../services/api';
import MobileHeader from './mobile/MobileHeader';
import MobileDrawer from './mobile/MobileDrawer';
import BottomNavigation from './mobile/BottomNavigation';
import { isPlanFeatureLocked } from '../../utils/planFeatures';
import './DashboardLayout.css';
import './mobile/MobileNavigation.css';

interface DashboardLayoutProps {
  children: React.ReactNode;
  noAssetRequired?: boolean;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children, noAssetRequired }) => {
  const { user, assets, currentAsset, setCurrentAsset, organizations, currentOrganization, setCurrentOrganization, logout } = useAuth();
  const { isMobile } = useMediaQuery();
  const [searchParams] = useSearchParams();
  const isEmbedMode = searchParams.get('embed') === 'true';
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isAssetDropdownOpen, setIsAssetDropdownOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isOrgDropdownOpen, setIsOrgDropdownOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [planFeatures, setPlanFeatures] = useState<Record<string, boolean>>({});
  const [planSlug, setPlanSlug] = useState<string>('');
  const [planName, setPlanName] = useState<string>('');
  const [planLoading, setPlanLoading] = useState(true);
  const [emailIssueCount, setEmailIssueCount] = useState(0);
  const [showHubTooltip, setShowHubTooltip] = useState(false);
  const [openSections, setOpenSections] = useState<Set<string>>(new Set());
  const [notifications, setNotifications] = useState<any[]>([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifUnread, setNotifUnread] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setPlanLoading(true);
    subscriptionAPI.getPlanLimits().then((res) => {
      const data = res.data?.data;
      if (data) {
        setPlanFeatures(data.features || {});
        setPlanSlug(data.plan_slug || '');
        setPlanName(data.plan_name || '');
      }
    }).catch(() => {}).finally(() => setPlanLoading(false));
  }, []);

  // Show Hub tooltip once on first login
  useEffect(() => {
    const key = 'ca_hub_tip_shown';
    if (!localStorage.getItem(key)) {
      const t = setTimeout(() => {
        setShowHubTooltip(true);
        localStorage.setItem(key, '1');
      }, 1500);
      return () => clearTimeout(t);
    }
  }, []);

  // Fetch recent sent notifications (email history) for bell icon
  useEffect(() => {
    const fetchNotifications = () => {
      alertsAPI.getEmailHistory({ limit: 8, days: 7 }).then(res => {
        const items = res.data?.data?.history || res.data?.data || [];
        const arr = Array.isArray(items) ? items : [];
        setNotifications(arr);
        // Count items sent in last 24h as "unread" indicator
        const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
        setNotifUnread(arr.filter((n: any) => new Date(n.sent_at || n.created_at).getTime() > oneDayAgo).length);
      }).catch(() => {});
    };
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Poll for email security issues (for sidebar badge)
  useEffect(() => {
    const checkEmailIssues = () => {
      emailMonitorAPI.hasIssues().then((res) => {
        if (res.data?.status === 'success') {
          setEmailIssueCount(res.data.data.issue_count || 0);
        }
      }).catch(() => {});
    };
    checkEmailIssues();
    const interval = setInterval(checkEmailIssues, 5 * 60 * 1000); // every 5 minutes
    return () => clearInterval(interval);
  }, []);

  const handleAssetChange = (asset: typeof currentAsset) => {
    setCurrentAsset(asset!);
    setIsAssetDropdownOpen(false);
  };

  const handleOrgChange = (org: typeof currentOrganization) => {
    setCurrentOrganization(org!);
    setIsOrgDropdownOpen(false);
    // Optionally reload to refresh assets for the new org
    window.location.reload();
  };

  const getActiveModule = (pathname: string): string => {
    const starts = (...prefixes: string[]) => prefixes.some((p) => pathname === p || pathname.startsWith(p + '/'));
    if (pathname === '/dashboard' || pathname === '/') return 'home';
    if (starts('/analytics', '/monitoring', '/alerts', '/share', '/events')) return 'website';
    if (starts('/app-analytics', '/docs/app-integration')) return 'appanalytics';
    if (starts('/security', '/email-monitor')) return 'security';
    if (starts('/servers', '/endpoints')) return 'servers';
    if (starts('/seo')) return 'seo';
    if (starts('/feedback') || pathname === '/integration/feedback') return 'feedback';
    if (starts('/billing', '/admin', '/users', '/organisation', '/assets', '/integration', '/settings', '/onboarding')) return 'settings';
    return 'home';
  };

  const activeModule = getActiveModule(location.pathname);
  const isHomePage = activeModule === 'home';

  const isActive = (path: string) => {
    // Exact match for paths that have siblings (like /servers and /servers/register)
    // These paths should not use startsWith to avoid both being active
    const exactMatchPaths = ['/servers', '/endpoints', '/events', '/billing'];
    if (exactMatchPaths.includes(path)) {
      return location.pathname === path;
    }
    // For other paths, use startsWith to handle child routes (e.g., /servers/123)
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Five core modules (Website, Security, Servers & Devices, SEO, Feedback),
  // App Analytics for mobile apps, and Settings. A child's own `feature`
  // overrides the module's for plan locking.
  const menuItems: { title: string; icon: string; path: string; feature?: string;
    children: { title: string; path: string; icon: string; feature?: string }[] }[] = [
    {
      title: 'Website',
      icon: 'bi-globe2',
      path: '/analytics',
      feature: 'analytics',
      children: [
        { title: 'Visitors', path: '/analytics/summary', icon: 'bi-graph-up' },
        { title: 'Pages', path: '/analytics/pageviews', icon: 'bi-file-earmark-bar-graph' },
        { title: 'Visitor Log', path: '/analytics/visitor-log', icon: 'bi-people' },
        { title: 'Uptime', path: '/monitoring/live', icon: 'bi-broadcast', feature: 'monitoring' },
        { title: 'Speed & Uptime Report', path: '/monitoring/performance', icon: 'bi-speedometer', feature: 'monitoring' },
        { title: 'Events & Conversions', path: '/analytics/events', icon: 'bi-lightning-charge' },
        { title: 'Campaigns', path: '/analytics/campaigns', icon: 'bi-megaphone' },
        { title: 'Reports', path: '/analytics/report', icon: 'bi-file-earmark-pdf' },
        { title: 'Alert History', path: '/alerts/history', icon: 'bi-envelope-paper' },
        { title: 'Share Score Widget', path: '/share/widget', icon: 'bi-share' },
      ],
    },
    {
      title: 'Security',
      icon: 'bi-shield-check',
      path: '/security',
      feature: 'security',
      children: [
        { title: 'Overview', path: '/security/ai-advisor', icon: 'bi-shield-shaded' },
        { title: 'Scans', path: '/security/scanning', icon: 'bi-search' },
        { title: 'Vulnerabilities', path: '/security/vulnerabilities', icon: 'bi-exclamation-triangle' },
        { title: 'AI Audit & Assistant', path: '/security/ai-audit', icon: 'bi-stars' },
        { title: 'Email Security', path: '/email-monitor/dashboard', icon: 'bi-envelope-check', feature: 'email_monitoring' },
        { title: 'Source Code Scanner', path: '/security/code-scan', icon: 'bi-code-slash' },
      ],
    },
    {
      title: 'Servers & Devices',
      icon: 'bi-hdd-rack',
      path: '/servers',
      feature: 'server_monitoring',
      children: [
        { title: 'Servers', path: '/servers', icon: 'bi-hdd-rack' },
        { title: 'Add Server', path: '/servers/register', icon: 'bi-plus-circle' },
        { title: 'Laptops & Desktops', path: '/endpoints', icon: 'bi-laptop', feature: 'endpoint_management' },
        { title: 'Device Threats', path: '/endpoints/threats', icon: 'bi-shield-exclamation', feature: 'endpoint_management' },
        { title: 'Device Policy', path: '/endpoints/policy', icon: 'bi-sliders', feature: 'endpoint_management' },
      ],
    },
    {
      title: 'SEO',
      icon: 'bi-search',
      path: '/seo',
      feature: 'seo',
      children: [
        { title: 'Summary', path: '/seo/summary', icon: 'bi-speedometer2' },
        { title: 'SEO Scanner', path: '/seo/scanner', icon: 'bi-search' },
        { title: 'AI Optimizer', path: '/seo/optimizer', icon: 'bi-stars', feature: 'seo_ai' },
        { title: 'Keyword Monitor', path: '/seo/keywords', icon: 'bi-bar-chart-line', feature: 'seo_rank_tracking' },
        { title: 'SEO for AI', path: '/seo/for-ai', icon: 'bi-robot' },
      ],
    },
    {
      title: 'Feedback',
      icon: 'bi-chat-heart',
      path: '/feedback',
      feature: 'feedback',
      children: [
        { title: 'Submissions', path: '/feedback', icon: 'bi-list-ul' },
        { title: 'Widget Settings', path: '/integration/feedback', icon: 'bi-sliders' },
      ],
    },
    {
      title: 'App Analytics',
      icon: 'bi-phone',
      path: '/app-analytics',
      feature: 'analytics',
      children: [
        { title: 'Overview', path: '/app-analytics/overview', icon: 'bi-speedometer2' },
        { title: 'Lifecycle Funnel', path: '/app-analytics/funnel', icon: 'bi-funnel' },
        { title: 'Users & Journeys', path: '/app-analytics/users', icon: 'bi-people' },
        { title: 'Tracking Plan & SDK', path: '/app-analytics/setup', icon: 'bi-diagram-3' },
        { title: 'Integration Docs', path: '/docs/app-integration', icon: 'bi-journal-code' },
      ],
    },
    {
      title: 'Settings',
      icon: 'bi-gear',
      path: '/assets',
      children: [
        { title: 'My Assets', path: '/assets', icon: 'bi-box-seam' },
        { title: 'Setup Checklist', path: '/onboarding', icon: 'bi-list-check' },
        { title: 'Notifications', path: '/settings/notifications', icon: 'bi-bell' },
        { title: 'Tracking Code', path: '/integration', icon: 'bi-code-square' },
        { title: 'Team', path: '/users', icon: 'bi-people' },
        { title: 'Organisation', path: '/organisation', icon: 'bi-building' },
        { title: 'Billing', path: '/billing', icon: 'bi-wallet2' },
        { title: 'Plans', path: '/billing/plans', icon: 'bi-grid-3x3-gap' },
        { title: 'Payment History', path: '/billing/payments', icon: 'bi-receipt' },
      ],
    },
  ];

  const isFeatureLocked = (feature?: string) => {
    if (!feature) return false;
    if (planLoading) return false;
    return isPlanFeatureLocked(planFeatures, feature);
  };

  // Map features to the minimum plan that unlocks them
  const featureMinPlan: Record<string, { plan: string; slug: string }> = {
    analytics: { plan: 'Free', slug: 'free' },
    monitoring: { plan: 'Starter', slug: 'starter' },
    security: { plan: 'Professional', slug: 'professional' },
    server_monitoring: { plan: 'Professional', slug: 'professional' },
    ai_advisor: { plan: 'Professional', slug: 'professional' },
    reports: { plan: 'Starter', slug: 'starter' },
    alerts: { plan: 'Starter', slug: 'starter' },
    event_points: { plan: 'Starter', slug: 'starter' },
    user_permissions: { plan: 'Professional', slug: 'professional' },
    email_monitoring: { plan: 'Starter', slug: 'starter' },
    endpoint_management: { plan: 'Enterprise', slug: 'enterprise' },
    seo: { plan: 'Starter', slug: 'starter' },
    seo_ai: { plan: 'Starter', slug: 'starter' },
    feedback: { plan: 'Starter', slug: 'starter' },
    api_access: { plan: 'Enterprise', slug: 'enterprise' },
    priority_support: { plan: 'Enterprise', slug: 'enterprise' },
  };

  const featureLabels: Record<string, string> = {
    analytics: 'Analytics Dashboard',
    monitoring: 'Website Monitoring',
    security: 'Cybersecurity Auditing',
    server_monitoring: 'Server Monitoring',
    ai_advisor: 'Intelligent Monitoring',
    reports: 'Reports & Exports',
    alerts: 'Alerts & Notifications',
    event_points: 'Register Events & Conversion Tracking',
    user_permissions: 'User Permissions & Roles',
    email_monitoring: 'Email Monitoring',
    endpoint_management: 'Endpoint Management',
    seo: 'SEO Scanner & Summary',
    seo_ai: 'AI SEO Optimizer',
    feedback: 'Customer Feedback Widget',
    api_access: 'API Access',
    priority_support: 'Priority Support',
  };

  // Check if the current page route belongs to a locked feature
  const featureRouteMap: Record<string, string[]> = {
    analytics: ['/analytics', '/app-analytics'],
    monitoring: ['/monitoring'],
    security: ['/security'],
    server_monitoring: ['/servers'],
    endpoint_management: ['/endpoints'],
    event_points: ['/events'],
    alerts: ['/alerts'],
    email_monitoring: ['/email-monitor'],
    seo: ['/seo'],
    feedback: ['/feedback', '/integration/feedback'],
  };

  const getLockedFeatureForRoute = (): string | null => {
    if (planLoading) return null;
    for (const [feature, paths] of Object.entries(featureRouteMap)) {
      if (isPlanFeatureLocked(planFeatures, feature) && paths.some(p => location.pathname.startsWith(p))) {
        return feature;
      }
    }
    return null;
  };

  const lockedFeature = getLockedFeatureForRoute();

  // Page body: spinner while the plan loads, upgrade card for locked modules,
  // otherwise the page. Used by both the desktop and mobile layouts.
  const pageContent = planLoading && Object.entries(featureRouteMap).some(([, paths]) => paths.some(p => location.pathname.startsWith(p))) ? (
            <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '60vh' }}>
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : lockedFeature ? (
            <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '60vh' }}>
              <div className="text-center" style={{ maxWidth: '550px' }}>
                <div className="mb-4">
                  <div className="bg-warning bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '100px', height: '100px' }}>
                    <i className="bi bi-lock-fill text-warning" style={{ fontSize: '3rem' }}></i>
                  </div>
                </div>
                <h3 className="fw-bold mb-2">{featureLabels[lockedFeature] || lockedFeature.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</h3>
                <p className="text-muted mb-3">
                  This feature is not available on your current <strong>{planName || 'Free'}</strong> plan.
                </p>
                {featureMinPlan[lockedFeature] && (
                  <div className="alert alert-info border-0 d-inline-block mb-4">
                    <i className="bi bi-info-circle me-2"></i>
                    Upgrade to <strong>{featureMinPlan[lockedFeature].plan}</strong> or higher to unlock {featureLabels[lockedFeature] || lockedFeature.replace(/_/g, ' ')}.
                  </div>
                )}
                <div>
                  <Link to="/billing/plans" className="btn btn-primary btn-lg px-5">
                    <i className="bi bi-arrow-up-circle me-2"></i>View Plans & Upgrade
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            children
          );

  // Allow billing and admin pages to render without an asset
  const isBillingOrAdminRoute = location.pathname.startsWith('/billing') ||
    location.pathname.startsWith('/organisation') ||
    location.pathname.startsWith('/users') ||
    location.pathname.startsWith('/assets');

  if (!currentAsset && !noAssetRequired && !isBillingOrAdminRoute && !isEmbedMode) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading your assets...</p>
        </div>
      </div>
    );
  }

  // Embed mode: render just the content without any chrome (sidebar, header, etc.)
  if (isEmbedMode) {
    return (
      <div style={{ background: '#f8fafc', minHeight: '100vh' }}>
        <main style={{ padding: '16px' }}>
          {children}
        </main>
      </div>
    );
  }

  // Mobile Layout
  if (isMobile) {
    return (
      <div className="mobile-dashboard-container">
        <MobileHeader
          onMenuClick={() => setIsDrawerOpen(true)}
          currentAsset={currentAsset}
          assets={assets}
          user={user}
          onAssetChange={(asset) => setCurrentAsset(asset)}
          onLogout={() => {
            logout();
            window.location.href = '/';
          }}
        />

        <MobileDrawer
          show={isDrawerOpen}
          onHide={() => setIsDrawerOpen(false)}
          menuItems={menuItems}
          user={user}
          organizations={organizations}
          currentOrganization={currentOrganization}
          onOrgChange={(org) => {
            setCurrentOrganization(org);
            window.location.reload();
          }}
          onLogout={() => {
            logout();
            window.location.href = '/';
          }}
        />

        <main className="mobile-content">
          {pageContent}
        </main>

        <BottomNavigation onMoreClick={() => setIsDrawerOpen(true)} />
      </div>
    );
  }

  // Map module names to menu item titles
  const moduleMenuTitleMap: Record<string, string[]> = {
    website: ['Website'],
    appanalytics: ['App Analytics'],
    security: ['Security'],
    servers: ['Servers & Devices'],
    seo: ['SEO'],
    feedback: ['Feedback'],
    settings: ['Settings'],
  };

  // Filter menu items for the current module
  const activeModuleTitles = moduleMenuTitleMap[activeModule] || [];
  const filteredMenuItems = isHomePage ? [] : menuItems.filter(item => activeModuleTitles.includes(item.title));

  // Notification helpers
  const notifTypeIcon = (type: string) => {
    if (type === 'alert') return 'bi-exclamation-triangle';
    if (type === 'report') return 'bi-file-earmark-bar-graph';
    if (type === 'downtime') return 'bi-x-circle';
    if (type === 'renewal') return 'bi-arrow-repeat';
    return 'bi-envelope';
  };
  const notifTypeColor = (type: string) => {
    if (type === 'alert') return '#f59e0b';
    if (type === 'report') return '#6366f1';
    if (type === 'downtime') return '#ef4444';
    if (type === 'renewal') return '#10b981';
    return '#64748b';
  };
  const timeAgoNotif = (dateStr: string) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  // Desktop Layout
  return (
    <div className={`dashboard-container ${isHomePage ? 'no-sidebar' : ''}`}>
      {/* Sidebar — hidden on home page */}
      {!isHomePage && (
      <aside className={`dashboard-sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`}>
        {/* Logo — always navigates to /dashboard */}
        <div className="sidebar-logo" style={{ cursor: 'pointer' }} onClick={() => navigate('/dashboard')}>
          <div className="logo-icon-wrapper">
            <span className="logo-letter">C</span>
          </div>
          {!isSidebarCollapsed && <span className="logo-text">CloudAyn</span>}
          <button
            className="sidebar-toggle"
            onClick={(e) => { e.stopPropagation(); setIsSidebarCollapsed(!isSidebarCollapsed); }}
          >
            <i className={`bi ${isSidebarCollapsed ? 'bi-chevron-right' : 'bi-chevron-left'}`}></i>
          </button>
        </div>

        {/* Home link — compact, no excess padding */}
        <Link
          to="/dashboard"
          className="nav-link"
          style={{ margin: '8px 10px 4px', borderRadius: '8px', padding: '8px 12px' }}
          title="Home"
        >
          <i className="bi bi-house-door"></i>
          {!isSidebarCollapsed && <span>Home</span>}
        </Link>

        {/* Module label — compact separator + title */}
        {filteredMenuItems[0] && (
          <div style={{ margin: '4px 10px 0', borderTop: '1px solid #f1f5f9', paddingTop: '8px' }}>
            {!isSidebarCollapsed && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '0 10px 4px' }}>
                <i className={`bi ${filteredMenuItems[0].icon}`} style={{ fontSize: '11px', color: '#94a3b8' }}></i>
                <span style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: '#94a3b8' }}>
                  {filteredMenuItems[0].title}
                  {filteredMenuItems[0].title === 'Security' && emailIssueCount > 0 && !isFeatureLocked('email_monitoring') && (
                    <span className="sidebar-alert-badge ms-2">{emailIssueCount}</span>
                  )}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Navigation — only current module's items, no big gaps */}
        <nav className="sidebar-nav" style={{ padding: '2px 0', flex: 1 }}>
          <ul className="nav-list" style={{ margin: 0, padding: 0 }}>
            {filteredMenuItems.flatMap((item, index) => {
              return (item.children || []).map((child, ci) => {
                const locked = isFeatureLocked(child.feature || item.feature);
                return (
                <li key={`${index}-${ci}`} style={{ listStyle: 'none' }}>
                  <Link
                    to={locked ? '/billing/plans' : child.path}
                    className={`nav-link ${isActive(child.path) ? 'active' : ''}`}
                    style={{ margin: '2px 10px', ...(locked ? { opacity: 0.5 } : {}) }}
                    title={locked ? `Upgrade to unlock ${child.title}` : child.title}
                  >
                    <i className={`bi ${locked ? 'bi-lock' : child.icon}`}></i>
                    {!isSidebarCollapsed && <span>{child.title}</span>}
                  </Link>
                </li>
                );
              });
            })}
          </ul>
        </nav>

        {/* Bottom: user info + logout — always visible, never scrolled away */}
        <div style={{
          borderTop: '1px solid #e9ecef',
          background: '#f8f9fa',
          padding: isSidebarCollapsed ? '10px 8px' : '10px 12px',
          flexShrink: 0,
        }}>
          {!isSidebarCollapsed ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div className="user-avatar" style={{ width: 34, height: 34, fontSize: 13, flexShrink: 0 }}>
                {user?.name.charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '0.82rem', color: '#212529', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name}</div>
                <div style={{ fontSize: '0.7rem', color: '#6c757d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.email}</div>
              </div>
              <button
                onClick={() => { logout(); window.location.href = '/'; }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6c757d', padding: '4px 6px', borderRadius: '6px', flexShrink: 0 }}
                title="Logout"
              >
                <i className="bi bi-box-arrow-right" style={{ fontSize: '15px' }}></i>
              </button>
            </div>
          ) : (
            <button
              onClick={() => { logout(); window.location.href = '/'; }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6c757d', width: '100%', display: 'flex', justifyContent: 'center', padding: '4px' }}
              title="Logout"
            >
              <i className="bi bi-box-arrow-right" style={{ fontSize: '18px' }}></i>
            </button>
          )}
        </div>
      </aside>
      )}

      {/* Main Content */}
      <div className="dashboard-main">
        {/* Top Header */}
        <header className="dashboard-header">
          <div className="header-left">
            {/* CloudAyn logo — only shown on home page (no sidebar) */}
            {isHomePage && (
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px', marginRight: '20px', cursor: 'pointer' }}
                onClick={() => navigate('/dashboard')}
              >
                <div className="logo-icon-wrapper" style={{ width: 32, height: 32, borderRadius: '8px', boxShadow: 'none' }}>
                  <span className="logo-letter" style={{ fontSize: '16px' }}>C</span>
                </div>
                <span className="logo-text" style={{ fontSize: '17px' }}>CloudAyn</span>
              </div>
            )}
            {/* Organization Selector */}
            <div className="dropdown me-3">
              <button
                className="btn btn-org-selector"
                type="button"
                onClick={() => setIsOrgDropdownOpen(!isOrgDropdownOpen)}
              >
                <i className="bi bi-building me-2"></i>
                <span>{currentOrganization?.name || 'Select Organization'}</span>
                <i className="bi bi-chevron-down ms-2"></i>
              </button>

              {isOrgDropdownOpen && (
                <>
                  <div
                    className="dropdown-backdrop"
                    onClick={() => setIsOrgDropdownOpen(false)}
                  ></div>
                  <div className="dropdown-menu show">
                    <h6 className="dropdown-header">Your Organizations</h6>
                    {organizations.map((org) => (
                      <button
                        key={org.id}
                        onClick={() => handleOrgChange(org)}
                        className={`dropdown-item ${currentOrganization?.id === org.id ? 'active' : ''}`}
                      >
                        <div>
                          <div className="fw-medium">{org.name}</div>
                          <small className="text-muted text-capitalize">{org.role}</small>
                        </div>
                      </button>
                    ))}
                    <hr className="dropdown-divider" />
                    <button
                      className="dropdown-item"
                      onClick={() => {
                        setIsOrgDropdownOpen(false);
                        navigate('/organisation');
                      }}
                    >
                      <i className="bi bi-arrow-repeat me-2"></i>Change Organization
                    </button>
                    {/* Create organization disabled - subscription integration pending */}
                  </div>
                </>
              )}
            </div>

            {/* Live Monitoring Hub Icon */}
            <div className="position-relative ms-2">
              <button
                className="btn btn-hub-icon"
                title="Live Monitoring Hub"
                onClick={() => { navigate('/monitoring/hub'); setShowHubTooltip(false); }}
                style={{
                  background: 'linear-gradient(135deg, #1e1e2e 0%, #2d2d44 100%)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '8px',
                  padding: '6px 12px',
                  color: '#e2e8f0',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  transition: 'all 0.2s',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLButtonElement).style.background = 'linear-gradient(135deg, #2d2d44 0%, #3d3d5c 100%)';
                  (e.currentTarget as HTMLButtonElement).style.color = '#fff';
                  (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.3)';
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLButtonElement).style.background = 'linear-gradient(135deg, #1e1e2e 0%, #2d2d44 100%)';
                  (e.currentTarget as HTMLButtonElement).style.color = '#e2e8f0';
                  (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.15)';
                }}
              >
                <i className="bi bi-grid-3x3-gap-fill"></i>
                <span style={{ fontWeight: 500 }}>Live Monitoring Hub</span>
                {/* Pulse dot (shows on first visit until tooltip dismissed) */}
                {showHubTooltip && (
                  <span style={{
                    position: 'absolute', top: '-4px', right: '-4px',
                    width: '10px', height: '10px', borderRadius: '50%',
                    background: '#f59e0b', border: '2px solid #fff',
                    animation: 'ca-pulse 1.5s ease-in-out infinite',
                  }}></span>
                )}
              </button>

              {/* First-login tooltip */}
              {showHubTooltip && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 10px)', left: '50%',
                  transform: 'translateX(-50%)',
                  background: '#1e1b4b', color: '#e0e7ff',
                  borderRadius: '10px', padding: '12px 16px',
                  width: '240px', boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                  zIndex: 9999, fontSize: '13px', lineHeight: 1.5,
                }}>
                  {/* Arrow */}
                  <div style={{
                    position: 'absolute', top: '-6px', left: '50%', transform: 'translateX(-50%)',
                    width: 0, height: 0,
                    borderLeft: '6px solid transparent', borderRight: '6px solid transparent',
                    borderBottom: '6px solid #1e1b4b',
                  }}></div>
                  <div className="d-flex align-items-start gap-2">
                    <i className="bi bi-display mt-1 text-warning flex-shrink-0" style={{ fontSize: '18px' }}></i>
                    <div>
                      <div className="fw-bold mb-1" style={{ color: '#c7d2fe' }}>Use on your big screen!</div>
                      <div style={{ color: '#a5b4fc', fontSize: '12px' }}>
                        Live Monitoring Hub shows all your assets at a glance. Perfect for a wall-mounted display.
                      </div>
                    </div>
                  </div>
                  <button
                    className="btn btn-sm w-100 mt-2"
                    style={{ background: '#6366f1', color: '#fff', fontSize: '12px', borderRadius: '6px' }}
                    onClick={() => { navigate('/monitoring/hub'); setShowHubTooltip(false); }}
                  >
                    <i className="bi bi-arrow-right-circle me-1"></i>Open Hub
                  </button>
                  <button
                    onClick={() => setShowHubTooltip(false)}
                    style={{ position: 'absolute', top: '8px', right: '8px', background: 'none', border: 'none', color: '#6366f1', cursor: 'pointer', fontSize: '14px', padding: '0' }}
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="header-right">
            {/* Asset Selector */}
            {currentAsset ? (
            <div className="dropdown">
              <button
                className="btn btn-asset-selector"
                type="button"
                onClick={() => setIsAssetDropdownOpen(!isAssetDropdownOpen)}
              >
                <i className="bi bi-box-seam me-2"></i>
                <span>{currentAsset.name}</span>
                <i className="bi bi-chevron-down ms-2"></i>
              </button>

              {isAssetDropdownOpen && (
                <>
                  <div
                    className="dropdown-backdrop"
                    onClick={() => setIsAssetDropdownOpen(false)}
                  ></div>
                  <div className="dropdown-menu show">
                    <h6 className="dropdown-header">Select Asset</h6>
                    {assets.map((asset) => (
                      <button
                        key={asset.id}
                        onClick={() => handleAssetChange(asset)}
                        className={`dropdown-item ${currentAsset.id === asset.id ? 'active' : ''}`}
                      >
                        <div>
                          <div className="fw-medium">{asset.name}</div>
                          <small className="text-muted">{asset.tracking_id}</small>
                        </div>
                      </button>
                    ))}
                    <hr className="dropdown-divider" />
                    <button
                      className="dropdown-item"
                      onClick={() => {
                        setIsAssetDropdownOpen(false);
                        navigate('/assets');
                      }}
                    >
                      <i className="bi bi-plus-circle me-2"></i>Manage Assets
                    </button>
                  </div>
                </>
              )}
            </div>
            ) : (
              <button className="btn btn-asset-selector" onClick={() => navigate('/assets')}>
                <i className="bi bi-plus-circle me-2"></i>
                <span>Add Asset</span>
              </button>
            )}

            {/* Notification Bell */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                style={{
                  position: 'relative',
                  background: showNotifications ? '#f1f5f9' : 'none',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '6px 10px',
                  cursor: 'pointer',
                  color: '#64748b',
                  display: 'flex',
                  alignItems: 'center',
                }}
                title="Notifications"
              >
                <i className="bi bi-bell" style={{ fontSize: '16px' }}></i>
                {notifUnread > 0 && (
                  <span style={{
                    position: 'absolute', top: '-5px', right: '-5px',
                    background: '#ef4444', color: '#fff', borderRadius: '50%',
                    fontSize: '10px', width: '16px', height: '16px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, border: '2px solid #fff', lineHeight: 1,
                  }}>
                    {notifUnread > 9 ? '9+' : notifUnread}
                  </span>
                )}
              </button>

              {showNotifications && (
                <>
                  <div
                    style={{ position: 'fixed', inset: 0, zIndex: 1040 }}
                    onClick={() => setShowNotifications(false)}
                  />
                  <div style={{
                    position: 'absolute', top: 'calc(100% + 8px)', right: 0,
                    width: '340px', background: '#fff', borderRadius: '12px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)', border: '1px solid #e2e8f0',
                    zIndex: 1050, overflow: 'hidden',
                  }}>
                    <div style={{ padding: '12px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e293b' }}>Recent Notifications</span>
                      <span style={{ display: 'flex', gap: '12px' }}>
                        <Link to="/settings/notifications" onClick={() => setShowNotifications(false)} style={{ fontSize: '0.75rem', color: '#6366f1', textDecoration: 'none' }}><i className="bi bi-gear me-1"></i>Settings</Link>
                        <Link to="/alerts/history" onClick={() => setShowNotifications(false)} style={{ fontSize: '0.75rem', color: '#6366f1', textDecoration: 'none' }}>View all</Link>
                      </span>
                    </div>
                    {notifications.length === 0 ? (
                      <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' }}>
                        <i className="bi bi-bell-slash d-block mb-2" style={{ fontSize: '24px' }}></i>
                        No notifications in the last 7 days
                      </div>
                    ) : (
                      <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
                        {notifications.map((n: any, i: number) => (
                          <div key={n.id || i} style={{
                            padding: '10px 16px', borderBottom: '1px solid #f8fafc',
                            display: 'flex', gap: '10px', alignItems: 'flex-start',
                          }}>
                            <div style={{
                              width: 32, height: 32, borderRadius: '8px', flexShrink: 0,
                              background: notifTypeColor(n.email_type) + '18',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              <i className={`bi ${notifTypeIcon(n.email_type)}`} style={{ fontSize: '13px', color: notifTypeColor(n.email_type) }}></i>
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {n.subject || 'Notification'}
                              </div>
                              <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '2px' }}>
                                {timeAgoNotif(n.sent_at || n.created_at)} · <span style={{ textTransform: 'capitalize' }}>{(n.email_type || '').replace(/_/g, ' ')}</span>
                              </div>
                            </div>
                            {n.status === 'sent' ? (
                              <i className="bi bi-check-circle-fill" style={{ fontSize: '12px', color: '#10b981', flexShrink: 0, marginTop: '4px' }}></i>
                            ) : (
                              <i className="bi bi-clock" style={{ fontSize: '12px', color: '#94a3b8', flexShrink: 0, marginTop: '4px' }}></i>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* User Menu */}
            <div className="dropdown">
              <button
                className="btn btn-user-menu"
                type="button"
                onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
              >
                <div className="user-avatar-sm">
                  {user?.name.charAt(0).toUpperCase()}
                </div>
              </button>

              {isUserDropdownOpen && (
                <>
                  <div
                    className="dropdown-backdrop"
                    onClick={() => setIsUserDropdownOpen(false)}
                  ></div>
                  <div className="dropdown-menu dropdown-menu-end show">
                    <div className="dropdown-header">
                      <div className="fw-semibold">{user?.name}</div>
                      <small className="text-muted">{user?.email}</small>
                    </div>
                    <hr className="dropdown-divider" />
                    <Link
                      className="dropdown-item"
                      to="/organisation"
                      onClick={() => setIsUserDropdownOpen(false)}
                    >
                      <i className="bi bi-building me-2"></i>My Organisation
                    </Link>
                    <Link
                      className="dropdown-item"
                      to="/users"
                      onClick={() => setIsUserDropdownOpen(false)}
                    >
                      <i className="bi bi-people me-2"></i>Users
                    </Link>
                    <hr className="dropdown-divider" />
                    <button
                      className="dropdown-item text-danger"
                      onClick={() => {
                        logout();
                        window.location.href = '/';
                      }}
                    >
                      <i className="bi bi-box-arrow-right me-2"></i>Logout
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="dashboard-content">
          {pageContent}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
